'use client';

import * as Sentry from '@sentry/nextjs';

import type { DocumentCategory } from '@kit/artists';
import { isTransientNetworkError } from '@kit/shared/lib/is-transient-network-error';
import { errorMessage } from '@kit/shared/validation';

import {
  normalizeBrowserMimeType,
  resolveUploadContentType,
} from './media-mime-rules';
import {
  createDancerMediaUploadUrlAction,
  finalizeDancerMediaUploadAction,
} from './server/media-actions';

/**
 * Browser-side direct-to-Supabase upload helper.
 *
 * Replaces the legacy single-action FormData upload, which silently failed
 * with HTTP 413 for files > 4.5 MB (Vercel server-action body cap). The
 * 3-step flow keeps every server-action body well under the limit:
 *
 *   1. createDancerMediaUploadUrlAction → tiny request, returns signed URL
 *   2. PUT file directly to Supabase storage (no Vercel involvement)
 *   3. finalizeDancerMediaUploadAction → tiny request, inserts the row
 *
 * Errors at any step are reported to Sentry with the upload context attached
 * (size, mime, mediaType). The previous shape just toasted a generic
 * message and dropped the stack on the floor.
 */
/**
 * Editorial category union — backed by the `artist_media_category_check`
 * CHECK constraint and the matching Zod schema in
 * `setup/_lib/server/media-actions.ts`. Pass `null` to clear; omit to
 * leave untouched at finalise.
 */
export type DancerMediaCategory =
  | 'audition'
  | 'stage_photo'
  | 'brand_editorial'
  | 'performance_video'
  | 'improvisation'
  | 'commercial'
  | 'choreography'
  | DocumentCategory;

/** 4:5 reframe crop chosen at upload, in the original image's pixel space,
 * plus that image's intrinsic dimensions. Persisted on the artist_media row
 * and applied as CSS at render (headshot only). */
export interface UploadDancerMediaCrop {
  x: number;
  y: number;
  w: number;
  h: number;
  width: number;
  height: number;
}

export interface UploadDancerMediaInput {
  file: File;
  mediaType: 'headshot' | 'photo' | 'video' | 'document';
  title?: string;
  description?: string;
  category?: DancerMediaCategory | null;
  crop?: UploadDancerMediaCrop;
  /**
   * Edit target (BAL-736). Set when a super admin uploads for ANOTHER dancer
   * via the consolidated editor; both upload steps forward it so the file lands
   * in the target's folder/rows. Undefined for self-service (writes auth.uid()).
   * The server refuses a non-admin passing a foreign id — this is a hint, not a
   * trust boundary.
   */
  targetUserId?: string;
}

export interface UploadDancerMediaSuccess {
  success: true;
  data: {
    id: string;
    storage_path: string | null;
    external_url: string | null;
    mime_type: string | null;
    file_name: string | null;
    title: string | null;
    media_type: string;
    signed_url: string | null;
  };
}

export interface UploadDancerMediaFailure {
  success: false;
  error: string;
  isExpectedError?: boolean;
}

export type UploadDancerMediaResult =
  | UploadDancerMediaSuccess
  | UploadDancerMediaFailure;

export async function uploadDancerMedia(
  input: UploadDancerMediaInput,
): Promise<UploadDancerMediaResult> {
  const { file, mediaType, title, description, category, crop, targetUserId } =
    input;

  const sentryContext = {
    mediaType,
    fileName: file.name,
    fileSize: file.size,
    mimeType: file.type,
    category,
  };

  try {
    // Step 1 — get signed upload URL.
    const urlResult = await createDancerMediaUploadUrlAction({
      mediaType,
      fileName: file.name,
      fileSize: file.size,
      // '' when the browser could not identify the file — the action's schema
      // requires a non-empty value, so collapse it to the generic type the
      // allowlist predicate already understands (BAL-747).
      mimeType: normalizeBrowserMimeType(file.type),
      category,
      targetUserId,
    });

    if (!urlResult.success) {
      // Validation errors are user-visible (file too large, wrong mime,
      // headshot already exists). Don't spam Sentry with those.
      if (!('isExpectedError' in urlResult && urlResult.isExpectedError)) {
        Sentry.captureMessage('Upload URL request failed', {
          level: 'error',
          extra: { ...sentryContext, error: urlResult.error },
        });
      }
      return urlResult;
    }

    const { signedUrl, path: storagePath } = urlResult.data;

    // Step 2 — direct PUT to Supabase storage. Bypasses Vercel entirely.
    const putResponse = await fetch(signedUrl, {
      method: 'PUT',
      headers: {
        // Not `file.type` verbatim: a .pdf the browser could not label is sent
        // as application/pdf, because the bucket's own allowed_mime_types has
        // no octet-stream and would 415 the PUT (BAL-747).
        'Content-Type': resolveUploadContentType({
          mediaType,
          mimeType: file.type,
          fileName: file.name,
        }),
        'x-upsert': 'false',
      },
      body: file,
    });

    if (!putResponse.ok) {
      const text = await putResponse.text().catch(() => '');
      const parsed = parseStorageError(text);
      Sentry.captureMessage('Direct storage upload failed', {
        level: 'error',
        extra: {
          ...sentryContext,
          storagePath,
          status: putResponse.status,
          response: text.slice(0, 500),
          parsedStatusCode: parsed.statusCode,
          parsedError: parsed.error,
        },
      });
      return {
        success: false,
        error: friendlyStorageError(parsed, putResponse.status),
        // Size-cap rejections are user-fixable (pick a smaller file);
        // surface them as expected so callers don't double-report to Sentry.
        isExpectedError: parsed.statusCode === '413',
      };
    }

    // Step 3 — finalise: insert the artist_media row.
    const finalizeResult = await finalizeDancerMediaUploadAction({
      mediaType,
      storagePath,
      fileName: file.name,
      fileSize: file.size,
      mimeType: normalizeBrowserMimeType(file.type),
      title,
      description,
      category,
      crop,
      targetUserId,
    });

    if (!finalizeResult.success) {
      // The file itself is already uploaded (step 2 succeeded); only the
      // finalise DB write failed. When that failure is a transient Supabase/
      // infra timeout, it is retry-worthy noise, not a bug: the server client
      // bounds every call with `AbortSignal.timeout` (see @kit/supabase
      // timeout-fetch) and supabase-js surfaces the abort as
      // "TimeoutError: The operation was aborted due to timeout". Classify it
      // with the shared detector so we (a) stop error-level Sentry spam for a
      // non-actionable infra blip and (b) tell the user to retry instead of
      // showing an opaque timeout string. Same precedent as the auth-wrapper
      // transient-timeout handling (BALLEE-FE) and the chat presence hooks.
      if (isTransientNetworkError(finalizeResult.error)) {
        return {
          success: false,
          error:
            'Saving your upload timed out — our servers were briefly slow. Your file uploaded fine; please try again in a moment.',
          isExpectedError: true,
        };
      }

      if (
        !('isExpectedError' in finalizeResult && finalizeResult.isExpectedError)
      ) {
        Sentry.captureMessage('Upload finalisation failed', {
          level: 'error',
          extra: { ...sentryContext, storagePath, error: finalizeResult.error },
        });
      }
      return finalizeResult;
    }

    return finalizeResult;
  } catch (err) {
    // This is the catch the original code silently swallowed. With Sentry
    // it now lands as a real, actionable issue.
    Sentry.captureException(err, { extra: sentryContext });
    return {
      success: false,
      error: errorMessage(err, 'Upload failed'),
    };
  }
}

interface ParsedStorageError {
  statusCode?: string;
  error?: string;
  message?: string;
}

/**
 * Supabase storage error responses are JSON of shape
 * `{ statusCode, error, message }`. The HTTP status is sometimes 400
 * even when the inner `statusCode` is 413 (payload too large) — that
 * mismatch is the reason we parse the body instead of trusting the
 * outer HTTP code.
 */
function parseStorageError(body: string): ParsedStorageError {
  if (!body) return {};
  try {
    const json = JSON.parse(body);
    if (json && typeof json === 'object') {
      return {
        statusCode:
          typeof json.statusCode === 'string' ? json.statusCode : undefined,
        error: typeof json.error === 'string' ? json.error : undefined,
        message: typeof json.message === 'string' ? json.message : undefined,
      };
    }
  } catch {
    // Non-JSON body — fall through.
  }
  return {};
}

function friendlyStorageError(
  parsed: ParsedStorageError,
  httpStatus: number,
): string {
  if (parsed.statusCode === '413') {
    return 'File exceeds the upload size limit. Please pick a smaller file or compress the video.';
  }
  if (parsed.error && parsed.message) {
    return `Upload failed: ${parsed.message}`;
  }
  if (parsed.error) {
    return `Upload failed: ${parsed.error}`;
  }
  return `Upload failed (${httpStatus})`;
}
